// Iter 82.1 / Phase E1.1 — Leave Accrual Operator console.
// Iter 104.0 — Adds "Provision Missing Balances" bulk zero-baseline
// operator surface on top of the existing accrual runner.
// Preview + Run for monthly leave accrual. Preview is a dry-run that
// projects what would post for the chosen period without writing any
// ledger entries. Run is idempotent per (employee × leave type × period).
import { useCallback, useEffect, useState } from "react";
import { Play, Eye, CheckCircle, Warning, UsersThree, ArrowClockwise } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

function defaultPeriod() {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export default function LeaveAccrualOperator() {
    const { can, user } = useAuth();
    const isPrimaryAdmin = Boolean(user?.is_primary_admin);
    const [period, setPeriod] = useState(defaultPeriod());
    const [empFilter, setEmpFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewedAt, setPreviewedAt] = useState(null);
    const [lastRun, setLastRun] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);

    // ── Iter 104.0 · Bulk zero-baseline provisioning ────────────────
    const [provDryRun, setProvDryRun] = useState(null);
    const [provLive, setProvLive] = useState(null);
    const [provLoading, setProvLoading] = useState(false);

    const runProvisionDryRun = async () => {
        setProvLoading(true);
        try {
            const { data } = await api.post("/leave/balance/bulk-provision", { dry_run: true });
            setProvDryRun(data);
            const t = data?.totals || {};
            toast.success(
                `Dry run · ${data.employees_targeted} employees · would create ${t.rows_created_total || 0} rows`,
            );
        } catch (err) {
            toast.error(formatApiError(err, "Provisioning dry run failed"));
        } finally {
            setProvLoading(false);
        }
    };

    const runProvisionLive = async () => {
        const targeted = provDryRun?.employees_targeted ?? "an unknown number of";
        const would = provDryRun?.totals?.rows_created_total ?? "?";
        if (!window.confirm(
            `Live provisioning will insert zero-baseline balance rows for ${targeted} employees ` +
            `(≈ ${would} rows). Idempotent — safe to re-run. Continue?`,
        )) return;
        setProvLoading(true);
        try {
            const { data } = await api.post("/leave/balance/bulk-provision", { dry_run: false });
            setProvLive(data);
            const t = data?.totals || {};
            toast.success(
                `Provisioned · created ${t.rows_created_total || 0} rows · ` +
                `already-present ${t.rows_already_present_total || 0} · ` +
                `errors ${t.error_count || 0}`,
            );
        } catch (err) {
            toast.error(formatApiError(err, "Provisioning live run failed"));
        } finally {
            setProvLoading(false);
        }
    };

    const fetchPreview = useCallback(async ({ silent = false } = {}) => {
        setLoading(true);
        try {
            const params = { period };
            // UAT Batch (2026-02) · Preview now honours the same
            // employee/type filters that the Run action accepts. Users
            // no longer need to keep two separate mental models.
            if (empFilter) params.employee_ids = empFilter.split(",").map((s) => s.trim()).filter(Boolean).join(",");
            if (typeFilter) params.leave_type_codes = typeFilter.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean).join(",");
            const { data } = await api.get("/leave/accrual/preview", { params });
            setPreview(data);
            setPreviewedAt(new Date());
            if (!silent) {
                const n = (data?.postings || []).length;
                toast.success(`Preview refreshed · ${n} projected postings for ${data?.period || period}`);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setLoading(false);
        }
    }, [period, empFilter, typeFilter]);

    // Initial mount + period/filter changes → silent auto-refresh (no
    // toast; users didn't ask for it). Explicit Preview button clicks
    // (see `onClick` handler below) fire with the default non-silent
    // path so the operator gets an unambiguous "refreshed" toast.
    useEffect(() => { Promise.resolve().then(() => fetchPreview({ silent: true })); }, [fetchPreview]);

    const run = async () => {
        if (!window.confirm(`Run monthly accrual for ${period}? This will post ledger entries for all eligible employees.`)) return;
        setRunning(true);
        try {
            const body = { period };
            if (empFilter) body.employee_ids = empFilter.split(",").map((s) => s.trim()).filter(Boolean);
            if (typeFilter) body.leave_type_codes = typeFilter.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
            const { data } = await api.post("/leave/accrual/run", body);
            setLastRun(data);
            toast.success(`Accrual posted: ${data.posted_count} entries, ${data.skipped_existing} skipped, ${data.error_count} errors`);
            fetchPreview({ silent: true });
        } catch (err) {
            toast.error(formatApiError(err, "Accrual run failed"));
        } finally {
            setRunning(false);
        }
    };

    const previewRows = preview?.postings || [];

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="leave-accrual-page">
            <PageHeader
                eyebrow="Leave Operations"
                title="Monthly Accrual Operator"
                description="Idempotent monthly leave accrual. Preview projects new postings; Run commits them to the ledger. APScheduler runs this automatically on the 1st of each month."
                actions={(
                    <div className="flex items-center gap-2">
                        <Input
                            type="text" placeholder="YYYY-MM"
                            data-testid="acc-period"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            className="w-32"
                        />
                        <Button variant="outline" size="sm" onClick={() => fetchPreview()} disabled={loading} data-testid="acc-preview-btn">
                            <Eye size={14} className="mr-1" /> {loading ? "…" : "Preview"}
                        </Button>
                        {previewedAt && (
                            <span
                                className="text-[11px] text-slate-500 hidden sm:inline-block"
                                title={previewedAt.toISOString()}
                                data-testid="acc-previewed-at"
                            >
                                Last previewed at {previewedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                            </span>
                        )}
                        {can("leave:admin") && (
                            <Button size="sm" onClick={run} disabled={running} data-testid="acc-run-btn">
                                <Play size={14} className="mr-1" /> {running ? "Running…" : "Run Accrual"}
                            </Button>
                        )}
                    </div>
                )}
            />

            {/* Iter 104.0 · Provision Missing Balances (zero-baseline skeleton) */}
            {can("leave:admin") && (
                <section
                    className="mt-5 bg-white rounded-xl border border-slate-200 p-4"
                    data-testid="prov-card"
                >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-start gap-3">
                            <UsersThree size={28} className="text-indigo-600 mt-0.5" weight="duotone" />
                            <div>
                                <h3 className="text-sm font-semibold text-slate-800">
                                    Provision Missing Leave Balances
                                    <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-wide">Iter 104.0</Badge>
                                </h3>
                                <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                                    Ensures every Active employee has skeleton balance rows for every applicable
                                    leave policy in the current fiscal year. Idempotent · never overwrites existing
                                    balances · posts a `zero_baseline` ledger entry per new row.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline" size="sm"
                                onClick={runProvisionDryRun}
                                disabled={provLoading}
                                data-testid="prov-dry-run-btn"
                            >
                                <Eye size={14} className="mr-1" />
                                {provLoading && !provLive ? "…" : "Dry Run — Preview Counts"}
                            </Button>
                            <Button
                                size="sm"
                                onClick={runProvisionLive}
                                disabled={provLoading || !isPrimaryAdmin}
                                title={isPrimaryAdmin ? "" : "Primary Super Admin only"}
                                data-testid="prov-run-btn"
                            >
                                <ArrowClockwise size={14} className="mr-1" />
                                {provLoading && provLive ? "Running…" : "Run Now"}
                            </Button>
                        </div>
                    </div>

                    {!isPrimaryAdmin && (
                        <p
                            className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block"
                            data-testid="prov-primary-admin-hint"
                        >
                            Live run requires the Primary Super Admin. Dry runs are open to any leave admin.
                        </p>
                    )}

                    {provDryRun && (
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="prov-dry-run-result">
                            <div className="bg-slate-50 border border-slate-200 rounded-md py-2 px-3 text-center">
                                <div className="text-[10px] uppercase text-slate-600 tracking-wide">Employees</div>
                                <div className="text-xl font-bold text-slate-900">{provDryRun.employees_targeted}</div>
                            </div>
                            <div className="bg-emerald-50 border border-emerald-200 rounded-md py-2 px-3 text-center">
                                <div className="text-[10px] uppercase text-emerald-700 tracking-wide">Would Create</div>
                                <div className="text-xl font-bold text-emerald-900">{provDryRun.totals?.rows_created_total ?? 0}</div>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded-md py-2 px-3 text-center">
                                <div className="text-[10px] uppercase text-slate-600 tracking-wide">Already Present</div>
                                <div className="text-xl font-bold text-slate-900">{provDryRun.totals?.rows_already_present_total ?? 0}</div>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded-md py-2 px-3 text-center">
                                <div className="text-[10px] uppercase text-slate-600 tracking-wide">Skipped (LoP/Inactive)</div>
                                <div className="text-xl font-bold text-slate-900">{provDryRun.totals?.rows_skipped_not_applicable_total ?? 0}</div>
                            </div>
                            <div className="bg-rose-50 border border-rose-200 rounded-md py-2 px-3 text-center">
                                <div className="text-[10px] uppercase text-rose-700 tracking-wide">Errors</div>
                                <div className="text-xl font-bold text-rose-900">{provDryRun.totals?.error_count ?? 0}</div>
                            </div>
                        </div>
                    )}

                    {provLive && (
                        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="prov-live-result">
                            <div className="flex items-start gap-2">
                                <CheckCircle size={16} className="text-emerald-700 mt-0.5" />
                                <div className="text-xs text-emerald-900">
                                    <div className="font-semibold">
                                        Live run complete · run_id <span className="font-mono">{provLive.run_id}</span>
                                    </div>
                                    <div className="mt-0.5">
                                        FY <span className="font-medium">{provLive.fy_code}</span> ·
                                        {" "}targeted <span className="font-semibold">{provLive.employees_targeted}</span> ·
                                        {" "}processed <span className="font-semibold">{provLive.employees_processed}</span> ·
                                        {" "}created <span className="font-semibold">{provLive.totals?.rows_created_total ?? 0}</span> ·
                                        {" "}already <span className="font-semibold">{provLive.totals?.rows_already_present_total ?? 0}</span> ·
                                        {" "}errors <span className="font-semibold">{provLive.totals?.error_count ?? 0}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            )}

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="acc-filters">
                    <h3 className="text-sm font-semibold text-slate-800 mb-2">Optional Filters (when running)</h3>
                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <label className="text-xs text-slate-600">Employee IDs (comma-separated)</label>
                            <Input
                                data-testid="acc-emp-filter"
                                placeholder="EMP-0001, EMP-0007"
                                value={empFilter}
                                onChange={(e) => setEmpFilter(e.target.value)}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-600">Leave Type Codes (comma-separated)</label>
                            <Input
                                data-testid="acc-type-filter"
                                placeholder="CL, SL"
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <p className="mt-3 text-xs text-slate-500">
                        Leave blank to process all eligible employees / leave types.
                        UAT Batch (2026-02): filters now apply to BOTH Preview and
                        Run — the manifest below reflects only the entered scope.
                    </p>
                </section>

                {preview && (
                    <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="acc-preview-card">
                        <h3 className="text-sm font-semibold text-slate-800 mb-3">Preview Manifest · {preview.period}</h3>
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-md py-2 px-3">
                                <div className="text-xs text-emerald-700">Would Post</div>
                                <div className="text-2xl font-bold text-emerald-900">{previewRows.length}</div>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded-md py-2 px-3">
                                <div className="text-xs text-slate-700">Already Done</div>
                                <div className="text-2xl font-bold text-slate-900">{preview.skipped_existing}</div>
                            </div>
                            <div className="bg-rose-50 border border-rose-200 rounded-md py-2 px-3">
                                <div className="text-xs text-rose-700">Errors</div>
                                <div className="text-2xl font-bold text-rose-900">{preview.error_count}</div>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-3">FY: <span className="font-medium">{preview.fiscal_year}</span> · Dry-run · No ledger entries written</p>
                    </section>
                )}
            </div>

            {lastRun && (
                <section className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="acc-last-run-banner">
                    <div className="flex items-start gap-3">
                        <CheckCircle size={20} className="text-emerald-700 mt-0.5" />
                        <div>
                            <h4 className="text-sm font-semibold text-emerald-900">Last Run · {lastRun.period}</h4>
                            <p className="text-xs text-emerald-800 mt-0.5">
                                Posted <span className="font-semibold">{lastRun.posted_count}</span> ledger entries ·
                                Skipped <span className="font-semibold">{lastRun.skipped_existing}</span> already-done ·
                                Errors <span className="font-semibold">{lastRun.error_count}</span>
                            </p>
                            {lastRun.error_count > 0 && (
                                <div className="mt-2 text-xs text-rose-900 space-y-1">
                                    {(lastRun.errors || []).slice(0, 5).map((e, i) => (
                                        <div key={i}><Warning size={10} className="inline mr-1" /> {e.employee_id} · {e.leave_type_code}: {e.error}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {previewRows.length > 0 && (
                <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="acc-projection-table">
                    <div className="px-4 py-3 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-800">Projected Postings ({previewRows.length})</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Employee Code</th>
                                    <th className="text-left px-3 py-2 font-medium">Name</th>
                                    <th className="text-left px-3 py-2 font-medium">Leave Type</th>
                                    <th className="text-left px-3 py-2 font-medium">Policy</th>
                                    <th className="text-right px-3 py-2 font-medium">Monthly Days</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previewRows.map((p, i) => (
                                    <tr key={`${p.employee_id}-${p.leave_type_code}-${i}`} className="border-t border-slate-100" data-testid={`acc-prev-row-${i}`}>
                                        <td className="px-3 py-2 font-mono text-xs">{p.employee_code}</td>
                                        <td className="px-3 py-2">{p.employee_name}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{p.leave_type_code}</td>
                                        <td className="px-3 py-2 text-slate-600 text-xs">{p.policy_name}</td>
                                        <td className="px-3 py-2 text-right font-semibold text-emerald-700">+{Number(p.monthly_days).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
}
